/*
  aro-finder.js (2026-09-30)
  One shared matcher for every search box and the scent finder.
  - aroIndex.load()      -> Promise of the full catalogue (finder-index view, cached per session)
  - aroMatch(q, list)    -> products ranked for a search box (designer names, nicknames, our names)
  - aroFind(q, list)     -> products ranked for the scent finder (also moods, seasons, notes, gender)
  - aroQuiz(answers, l)  -> products ranked for the "Help me choose" questions on the same page
  No outside services: it only reads our own product data.
*/
(function () {
  'use strict';

  // ---------- text helpers ----------
  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/&/g, ' and ')
      .replace(/['’`]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  function has(hay, needle) { return needle && (' ' + hay + ' ').indexOf(' ' + needle + ' ') > -1; }
  function contains(hay, needle) { return needle && hay.indexOf(needle) > -1; }

  // Nicknames and short forms shoppers type, mapped to how the designer name is written.
  var SYN = {
    'ysl': 'yves saint laurent', 'saint laurent': 'yves saint laurent',
    'mfk': 'maison francis kurkdjian', 'kurkdjian': 'maison francis kurkdjian',
    'br540': 'baccarat rouge 540', 'br 540': 'baccarat rouge 540', 'baccarat': 'baccarat rouge 540', 'baccarat rouge': 'baccarat rouge 540',
    'pdm': 'parfums de marly', 'marly': 'parfums de marly',
    'tf': 'tom ford', 'jpg': 'jean paul gaultier', 'gaultier': 'jean paul gaultier',
    'lv': 'louis vuitton', 'dg': 'dolce and gabbana', 'd and g': 'dolce and gabbana', 'dolce': 'dolce and gabbana',
    'ck': 'calvin klein', 'hugo': 'hugo boss', 'boss': 'hugo boss',
    'armani': 'giorgio armani', 'ga': 'giorgio armani',
    'santal': 'santal 33', 'bleu': 'bleu de chanel', 'le labo santal': 'santal 33',
    'avantus': 'aventus', 'aventis': 'aventus', 'sauvage elixer': 'sauvage elixir', 'savage': 'sauvage'
  };
  function expand(q) {
    var n = norm(q);
    var out = [n];
    Object.keys(SYN).forEach(function (k) {
      if (has(n, k)) out.push(norm(n.replace(new RegExp('(^| )' + k + '( |$)'), '$1' + SYN[k] + '$2')));
    });
    return out.filter(function (v, i, a) { return v && a.indexOf(v) === i; });
  }

  // ---------- catalogue ----------
  function prep(p) {
    if (p.__n) return p;
    var ib = String(p.ib || p.inspired_by || '');
    var isOriginal = /aromara/i.test(ib) || /bundle/i.test(ib);
    var parts = ib.split(/\s+by\s+/i);
    p.__n = {
      title: norm(p.t || p.title),
      name: isOriginal ? '' : norm(parts[0] || ''),
      house: isOriginal ? '' : norm(parts.slice(1).join(' by ')),
      tags: (p.tg || p.tags || []).map(norm),
      notes: norm(p.n || ''),
      original: isOriginal
    };
    p.__tagset = {};
    p.__n.tags.forEach(function (t) { p.__tagset[t] = 1; });
    return p;
  }
  function isSellable(p) {
    if (p.av === false || p.available === false) return false;
    var tg = p.__tagset || {};
    if (tg['waitlist'] || tg['coming soon']) return false;
    return true;
  }
  function isSet(p) {
    var u = String(p.u || p.url || '');
    return /set|bundle/.test(u) || (p.__tagset && (p.__tagset['bundle'] || p.__tagset['gift']));
  }

  var aroIndex = {
    _p: null,
    load: function () {
      if (this._p) return this._p;
      var KEY = 'aro_finder_idx_v1';
      try {
        var c = sessionStorage.getItem(KEY);
        if (c) { var list = JSON.parse(c); this._p = Promise.resolve(list); return this._p; }
      } catch (e) {}
      function page(n) { return fetch('/collections/shop-all?view=finder-index&page=' + n).then(function (r) { return r.json(); }); }
      this._p = page(1).then(function (d1) {
        var jobs = [];
        for (var i = 2; i <= (d1.pages || 1); i++) jobs.push(page(i));
        return Promise.all(jobs).then(function (rest) {
          var all = d1.products || [];
          rest.forEach(function (d) { all = all.concat(d.products || []); });
          try { sessionStorage.setItem(KEY, JSON.stringify(all)); } catch (e) {}
          return all;
        });
      });
      return this._p;
    }
  };

  // ---------- search box ranking ----------
  function scoreSearch(p, variants, tokens) {
    var n = p.__n, s = 0, why = [];
    variants.forEach(function (q) {
      if (!q) return;
      if (n.name && n.name === q) { s = Math.max(s, 220); why.push('name'); }
      else if (n.name && n.name.indexOf(q) === 0) { s = Math.max(s, 150); }
      else if (n.name && contains(n.name, q)) { s = Math.max(s, 120); }
      if (n.title === q) s = Math.max(s, 200);
      else if (contains(n.title, q)) s = Math.max(s, 110);
      if (n.house && (n.house === q || contains(n.house, q))) s = Math.max(s, 95);
      if (n.house && contains(q, n.house) && n.name && contains(q, n.name)) s = Math.max(s, 240);
    });
    var hit = 0;
    tokens.forEach(function (t) {
      if (t.length < 3) return;
      var got = false;
      if (n.name && has(n.name, t)) { s += 30; got = true; }
      if (n.house && has(n.house, t)) { s += 22; got = true; }
      if (has(n.title, t)) { s += 30; got = true; }
      if (p.__tagset[t]) { s += 14; got = true; }
      if (n.notes && has(n.notes, t)) { s += 10; got = true; }
      if (got) hit++;
    });
    if (s > 0 && hit && hit === tokens.filter(function (t) { return t.length >= 3; }).length) s += 20;
    // prefer the plain version over flankers when the shopper did not ask for one
    if (s > 0 && n.name) {
      var extra = n.name.split(' ').filter(function (w) { return w && variants[0].indexOf(w) === -1; }).length;
      s -= extra * 6;
    }
    if (s > 0) {
      if (p.__tagset['bestseller']) s += 6;
      if (p.rc) s += Math.min(8, Math.log(1 + p.rc) * 2);
    }
    return { s: s, why: why };
  }

  function aroMatch(q, list, opts) {
    opts = opts || {};
    var variants = expand(q);
    if (!variants.length) return [];
    var tokens = variants.join(' ').split(' ').filter(Boolean).filter(function (v, i, a) { return a.indexOf(v) === i; });
    var out = [];
    (list || []).forEach(function (p) {
      prep(p);
      if (opts.sellableOnly !== false && !isSellable(p)) return;
      var r = scoreSearch(p, variants, tokens);
      if (r.s > 0) out.push({ p: p, score: r.s });
    });
    out.sort(function (a, b) { return b.score - a.score; });
    // one entry per product (the index can hold the same product under two addresses)
    var seen = {};
    return out.map(function (x) { return x.p; }).filter(function (p) {
      var m = String(p.u || p.url || '').match(/\/products\/([^?#/]+)/);
      var k = m ? m[1] : (p.u || p.url);
      if (seen[k]) return false;
      seen[k] = 1;
      return true;
    });
  }

  // ---------- scent finder ----------
  // Words shoppers use, mapped to our product tags (tag names are lower case after norm()).
  var CONCEPTS = [
    { w: ['fresh', 'clean', 'light', 'airy', 'crisp', 'breezy'], t: { 'fresh': 3, 'citrus': 2, 'aquatic': 2, 'aromatic': 1, 'green': 1, 'clean': 2 } },
    { w: ['office', 'work', 'daytime', 'day', 'everyday', 'signature', 'versatile', 'gym', 'safe'], t: { 'everyday': 3, 'fresh': 2, 'all season': 2, 'aromatic': 1, 'day': 2 } },
    { w: ['summer', 'spring', 'sunny', 'holiday', 'beach', 'hot', 'warm weather'], t: { 'spring summer': 3, 'summer': 3, 'spring': 2, 'fresh': 2, 'citrus': 2, 'aquatic': 2, 'tropical': 2, 'marine': 2 } },
    { w: ['winter', 'autumn', 'fall', 'cold', 'christmas', 'cosy', 'cozy', 'snug'], t: { 'autumn winter': 3, 'winter': 3, 'autumn': 2, 'warm spicy': 2, 'ambery': 2, 'vanilla': 2, 'spicy': 1, 'woody': 1, 'warm': 2 } },
    { w: ['night', 'evening', 'date', 'sexy', 'seductive', 'romantic', 'party', 'club', 'clubbing', 'going out', 'sensual'], t: { 'evening': 3, 'date night': 3, 'special occasion': 1, 'ambery': 2, 'oud': 1, 'vanilla': 1, 'sensual': 2, 'romantic': 2, 'leather': 1 } },
    { w: ['wedding', 'special', 'formal', 'occasion', 'event'], t: { 'special occasion': 3, 'floral': 1, 'woody': 1 } },
    { w: ['sweet', 'sugary', 'candy', 'dessert', 'gourmand', 'edible', 'yummy', 'caramel', 'praline', 'marshmallow'], t: { 'sweet': 3, 'gourmand': 3, 'vanilla': 2, 'creamy': 2, 'caramel': 2, 'praline': 2 } },
    { w: ['vanilla'], t: { 'vanilla': 4, 'sweet': 1, 'creamy': 1 } },
    { w: ['oud', 'arabic', 'arabian', 'middle eastern', 'agarwood'], t: { 'oud': 4, 'smoky': 1, 'woody': 1, 'leather': 1 } },
    { w: ['smoky', 'smokey', 'smoke', 'incense', 'dark'], t: { 'smoky': 3, 'incense': 3, 'leather': 1, 'tobacco': 1, 'dark': 2 } },
    { w: ['woody', 'wood', 'sandalwood', 'cedar', 'cedarwood', 'vetiver'], t: { 'woody': 3, 'sandalwood': 3, 'cedar': 2, 'cedarwood': 2, 'vetiver': 2 } },
    { w: ['floral', 'flower', 'flowery', 'flowers', 'jasmine', 'peony', 'tuberose'], t: { 'floral': 3, 'white floral': 2, 'rose': 1, 'jasmine': 2 } },
    { w: ['rose', 'roses'], t: { 'rose': 4, 'floral': 1 } },
    { w: ['fruity', 'fruit', 'berry', 'berries', 'peach', 'mango', 'pineapple', 'apple', 'cherry', 'juicy'], t: { 'fruity': 3, 'tropical': 1, 'cherry': 2, 'peach': 2, 'mango': 2 } },
    { w: ['citrus', 'lemon', 'bergamot', 'orange', 'grapefruit', 'zesty', 'mandarin', 'lime'], t: { 'citrus': 4, 'fresh': 1 } },
    { w: ['aquatic', 'marine', 'ocean', 'sea', 'salty', 'water'], t: { 'aquatic': 4, 'marine': 3, 'salty': 2, 'fresh': 1 } },
    { w: ['spicy', 'spice', 'pepper', 'cinnamon', 'saffron', 'cardamom'], t: { 'spicy': 3, 'warm spicy': 3, 'fresh spicy': 2, 'saffron': 2, 'pepper': 2 } },
    { w: ['musk', 'musky', 'skin', 'soft'], t: { 'musky': 3, 'musk': 3, 'powdery': 1, 'clean': 1 } },
    { w: ['powdery', 'iris', 'powder', 'cosmetic', 'lipstick'], t: { 'powdery': 4, 'iris': 3 } },
    { w: ['tobacco'], t: { 'tobacco': 5 } },
    { w: ['leather'], t: { 'leather': 5 } },
    { w: ['coffee'], t: { 'coffee': 5 } },
    { w: ['honey'], t: { 'honey': 5 } },
    { w: ['coconut'], t: { 'coconut': 5, 'tropical': 1 } },
    { w: ['amber', 'ambery', 'resin', 'resinous'], t: { 'ambery': 4, 'amber': 4, 'resinous': 2 } },
    { w: ['creamy', 'milky', 'soft'], t: { 'creamy': 4, 'milky': 3 } },
    { w: ['boozy', 'rum', 'cognac', 'whisky', 'whiskey'], t: { 'boozy': 5 } },
    { w: ['niche'], t: { 'niche': 3 } },
    { w: ['green', 'grassy', 'herbal', 'tea'], t: { 'green': 4, 'aromatic': 2 } }
  ];
  var STOP_NAMES = { 'her': 1, 'him': 1, 'man': 1, 'men': 1, 'woman': 1, 'women': 1, 'you': 1, 'y': 1, 'she': 1, 'he': 1, 'love': 1, 'good girl': 0 };
  // tags that describe how a scent smells (used to explain "similar" picks)
  var FAMILY = {};
  ['fresh', 'woody', 'sweet', 'spicy', 'vanilla', 'aromatic', 'floral', 'smoky', 'rose', 'gourmand', 'fruity', 'oud', 'powdery', 'warm spicy', 'ambery', 'leather', 'aquatic', 'citrus', 'musky', 'tobacco', 'tropical', 'amber', 'creamy', 'green', 'marine', 'coconut', 'honey', 'coffee', 'iris', 'saffron', 'incense', 'vetiver', 'sandalwood', 'cherry', 'boozy'].forEach(function (t) { FAMILY[t] = 1; });
  var HIM = ['him', 'men', 'man', 'male', 'mens', 'boyfriend', 'husband', 'dad', 'father', 'masculine', 'guy', 'brother', 'son'];
  var HER = ['her', 'women', 'woman', 'female', 'womens', 'ladies', 'girlfriend', 'wife', 'mum', 'mom', 'mother', 'feminine', 'sister', 'daughter'];
  var UNI = ['unisex', 'anyone', 'neutral', 'shared', 'couple'];

  function readQuery(q) {
    var n = norm(q);
    var words = n.split(' ');
    var want = {}, found = [];
    CONCEPTS.forEach(function (c) {
      c.w.forEach(function (w) {
        if (has(n, norm(w))) {
          found.push(w);
          Object.keys(c.t).forEach(function (t) { var k = norm(t); want[k] = Math.max(want[k] || 0, c.t[t]); });
        }
      });
    });
    var gender = null;
    if (words.some(function (w) { return HIM.indexOf(w) > -1; })) gender = 'for him';
    if (words.some(function (w) { return HER.indexOf(w) > -1; })) gender = gender ? null : 'for her';
    if (words.some(function (w) { return UNI.indexOf(w) > -1; })) gender = 'unisex';
    return { n: n, words: words, want: want, found: found, gender: gender };
  }

  function similar(a, b) {
    // share of tags and notes two scents have in common
    var ta = a.__n.tags.concat(a.__n.notes.split(' ')), tb = {};
    b.__n.tags.concat(b.__n.notes.split(' ')).forEach(function (t) { if (t && t.length > 2) tb[t] = 1; });
    var hit = 0, tot = 0;
    ta.forEach(function (t) { if (t && t.length > 2) { tot++; if (tb[t]) hit++; } });
    return tot ? hit / tot : 0;
  }

  function aroFind(q, list) {
    list = (list || []).map(prep).filter(function (p) { return isSellable(p) && !isSet(p); });
    var info = readQuery(q);
    var named = aroMatch(q, list).filter(function (p) { return !p.__n.original; });
    // A designer scent was named if the top search hit matches on its name or house strongly.
    var anchor = null;
    if (named.length) {
      var top = named[0], tn = top.__n;
      var v = expand(q);
      // a short or generic name ("her", "y") is not enough to call it a named perfume
      var weak = !tn.name || tn.name.length < 4 || STOP_NAMES[tn.name];
      if (!weak && v.some(function (x) { return has(x, tn.name) || (contains(tn.name, x) && x.length > 3); })) anchor = top;
    }
    var houseHits = named.filter(function (p) { return p.__n.house && expand(q).some(function (x) { return contains(x, p.__n.house); }); });

    var scored = list.map(function (p) {
      var s = 0, reasons = [];
      Object.keys(info.want).forEach(function (t) {
        if (p.__tagset[t]) { s += 12 * info.want[t]; reasons.push(t); }
      });
      info.words.forEach(function (w) {
        if (w.length > 3 && p.__n.notes && has(p.__n.notes, w)) { s += 18; reasons.push(w); }
      });
      if (info.gender) {
        var g = p.__tagset;
        if (info.gender === 'for him' && g['for her'] && !g['for him'] && !g['unisex']) s -= 200;
        if (info.gender === 'for her' && g['for him'] && !g['for her'] && !g['unisex']) s -= 200;
        if (g[info.gender]) s += 10;
      }
      if (anchor && p !== anchor) {
        s += Math.round(similar(anchor, p) * 90);
        // say what the two have in common (families first, then shared notes)
        anchor.__n.tags.forEach(function (t) { if (p.__tagset[t] && FAMILY[t]) reasons.push(t); });
      }
      if (houseHits.indexOf(p) > -1) s += 60;
      if (s > 0) {
        if (p.__tagset['bestseller']) s += 10;
        if (p.r && p.rc) s += Math.min(10, (p.r - 3.5) * 4 + Math.log(1 + p.rc));
      }
      return { p: p, s: s, reasons: reasons.filter(function (v, i, a) { return a.indexOf(v) === i; }) };
    }).filter(function (x) { return x.s > 0; });

    scored.sort(function (a, b) { return b.s - a.s; });
    var picks = [];
    if (anchor) picks.push({ p: anchor, s: 9999, reasons: [], anchor: true });
    scored.forEach(function (x) { if (!anchor || x.p !== anchor) picks.push(x); });
    return { picks: picks, anchor: anchor, info: info, houseHits: houseHits };
  }

  // ---------- quiz ("Help me choose") ----------
  // Four taps: who it is for, when, which smell, how strong. Each answer adds weight to our
  // product tags (lower case after norm()). Keys are short so results can live in the address
  // (?a=him.evening.woody.bold) and be linked from emails.
  var QUIZ = {
    for: {
      him: { gender: 'for him' },
      her: { gender: 'for her' },
      any: { gender: 'unisex' }
    },
    when: {
      day: { t: { 'everyday': 3, 'all season': 2, 'fresh': 2, 'aromatic': 1 }, fit: ['everyday', 'all season'] },
      evening: { t: { 'evening': 3, 'date night': 3, 'sensual': 2, 'ambery': 1, 'sweet': 1 }, fit: ['evening', 'date night'] },
      special: { t: { 'special occasion': 3, 'evening': 1 }, fit: ['special occasion'] },
      mix: { t: { 'all season': 3, 'everyday': 2 }, fit: ['all season', 'everyday'] }
    },
    family: {
      fresh: { t: { 'fresh': 3, 'citrus': 3, 'aquatic': 3, 'marine': 2, 'aromatic': 2, 'green': 2 } },
      sweet: { t: { 'sweet': 3, 'vanilla': 3, 'gourmand': 3, 'creamy': 2, 'praline': 2, 'coconut': 1, 'honey': 1 } },
      woody: { t: { 'woody': 3, 'warm spicy': 3, 'spicy': 2, 'ambery': 2, 'amber': 2, 'sandalwood': 2, 'vetiver': 1 } },
      floral: { t: { 'floral': 3, 'rose': 3, 'fruity': 3, 'white floral': 2, 'powdery': 1, 'tropical': 1 } },
      dark: { t: { 'oud': 3, 'smoky': 3, 'leather': 3, 'tobacco': 3, 'incense': 2, 'dark': 2 } },
      any: { t: {} }
    },
    strength: {
      soft: { t: { 'fresh': 2, 'musky': 2, 'musk': 2, 'powdery': 2, 'citrus': 1 }, minus: ['oud', 'smoky', 'leather', 'tobacco'] },
      mid: { t: { 'aromatic': 1, 'floral': 1, 'woody': 1 } },
      bold: { t: { 'oud': 2, 'smoky': 2, 'leather': 2, 'tobacco': 2, 'ambery': 1, 'spicy': 1, 'niche': 2 } }
    }
  };

  function handleOf(p) {
    var m = String(p.u || p.url || '').match(/\/products\/([^?#/]+)/);
    return m ? m[1] : (p.u || p.url);
  }

  function toArr(v) { return Array.isArray(v) ? v : (v ? [v] : []); }

  // Answers can hold one key or several (the "when" and "smell" questions allow more than one).
  function aroQuiz(ans, list) {
    ans = ans || {};
    list = (list || []).map(prep).filter(function (p) { return isSellable(p) && !isSet(p) && !p.__n.original; });
    var who = QUIZ.for[ans.for], str = QUIZ.strength[ans.strength];
    var whens = toArr(ans.when).map(function (k) { return QUIZ.when[k]; }).filter(Boolean);
    var fams = toArr(ans.family).filter(function (k) { return k !== 'any' && QUIZ.family[k]; });
    var wt = {}, fitTags = [];
    whens.forEach(function (w) {
      Object.keys(w.t).forEach(function (t) { wt[t] = Math.max(wt[t] || 0, w.t[t]); });
      w.fit.forEach(function (t) { if (fitTags.indexOf(t) < 0) fitTags.push(t); });
    });
    var scored = list.map(function (p) {
      var g = p.__tagset, s = 0, lead = [], more = [], fit = '', hits = [];
      // each chosen smell adds its own score, so a scent that fits two of them ranks higher;
      // the first matching tag of each smell leads the reasons shown on the card
      fams.forEach(function (k) {
        var f = QUIZ.family[k], fs = 0, first = true;
        Object.keys(f.t).forEach(function (t) {
          if (!g[t]) return;
          fs += 18 * f.t[t];
          if (lead.indexOf(t) > -1 || more.indexOf(t) > -1) return;
          if (first) { lead.push(t); first = false; } else more.push(t);
        });
        if (fs) { s += fs; hits.push(k); }
      });
      var reasons = lead.concat(more);
      Object.keys(wt).forEach(function (t) { if (g[t]) s += 8 * wt[t]; });
      fitTags.some(function (t) { if (g[t]) { fit = t; return true; } return false; });
      if (str) {
        Object.keys(str.t).forEach(function (t) { if (g[t]) s += 7 * str.t[t]; });
        (str.minus || []).forEach(function (t) { if (g[t]) s -= 30; });
      }
      if (who) {
        if (who.gender === 'for him' && g['for her'] && !g['for him'] && !g['unisex']) s -= 400;
        if (who.gender === 'for her' && g['for him'] && !g['for her'] && !g['unisex']) s -= 400;
        if (g[who.gender]) s += who.gender === 'unisex' ? 14 : 30;
        else if (g['unisex']) s += 8;
      }
      if (g['bestseller']) s += 12;
      if (p.r && p.rc) s += Math.min(12, (p.r - 3.5) * 4 + Math.log(1 + p.rc) * 1.5);
      return { p: p, s: s, reasons: reasons, fit: fit, fams: hits };
    }).filter(function (x) { return x.s > 0; });
    scored.sort(function (a, b) { return b.s - a.s; });
    var seen = {};
    return scored.filter(function (x) {
      var k = handleOf(x.p);
      if (seen[k]) return false;
      seen[k] = 1;
      return true;
    });
  }

  // The top n, making sure each chosen smell is represented when more than one was picked.
  function aroQuizPicks(ranked, ans, n) {
    n = n || 3;
    var fams = toArr(ans && ans.family).filter(function (k) { return k !== 'any'; });
    if (fams.length < 2) return ranked.slice(0, n);
    var picks = [];
    fams.forEach(function (k) {
      if (picks.length >= n) return;
      for (var i = 0; i < ranked.length; i++) {
        var r = ranked[i];
        if (r.fams.indexOf(k) > -1 && picks.indexOf(r) < 0) { picks.push(r); break; }
      }
    });
    ranked.forEach(function (r) { if (picks.length < n && picks.indexOf(r) < 0) picks.push(r); });
    picks.sort(function (a, b) { return b.s - a.s; });
    return picks;
  }

  // ---------- quiz screens (shared by the Find your scent page and the bundle builder) ----------
  // The panel frame and its styles come from snippets/aro-quiz-panel.liquid.
  var Q_ICON = {
    sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>',
    moon: '<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"/>',
    spark: '<path d="M12 3l1.8 5.4L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.6z"/><path d="M19 17l.7 1.8L21 19.5l-1.3.7L19 22l-.7-1.8L17 19.5l1.3-.7z"/>',
    loop: '<path d="M17 2l3 3-3 3"/><path d="M4 11V9a4 4 0 0 1 4-4h12"/><path d="M7 22l-3-3 3-3"/><path d="M20 13v2a4 4 0 0 1-4 4H4"/>',
    leaf: '<path d="M5 19c0-8 5-13 15-14-1 10-6 15-14 15"/><path d="M5 19l7-7"/>',
    sweet: '<circle cx="12" cy="12" r="4"/><path d="M8.4 10.6 4 8v8l4.4-2.6M15.6 10.6 20 8v8l-4.6-2.6"/>',
    flame: '<path d="M12 22a7 7 0 0 0 7-7c0-4-3-6-4-10-2 2-3 4-3 6-1-1-2-2-2-4-2 2-5 5-5 8a7 7 0 0 0 7 7z"/>',
    flower: '<circle cx="12" cy="12" r="2.5"/><path d="M12 9.5C10 7.5 10 4 12 3c2 1 2 4.5 0 6.5zM14.5 12c2-2 5.5-2 6.5 0-1 2-4.5 2-6.5 0zM12 14.5c2 2 2 5.5 0 6.5-2-1-2-4.5 0-6.5zM9.5 12c-2 2-5.5 2-6.5 0 1-2 4.5-2 6.5 0z"/>',
    smoke: '<path d="M8 21c-2-2 2-4 0-6s2-4 0-6"/><path d="M13 21c-2-2 2-4 0-6s2-4 0-6s2-4 0-6"/><path d="M18 21c-2-2 2-4 0-6s2-4 0-6"/>',
    star: '<path d="M12 3l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6-4.6-4.1 6.1-.6z"/>',
    b1: '<path d="M6 18v-4"/><path d="M12 18v-8" opacity=".3"/><path d="M18 18V6" opacity=".3"/>',
    b2: '<path d="M6 18v-4"/><path d="M12 18v-8"/><path d="M18 18V6" opacity=".3"/>',
    b3: '<path d="M6 18v-4"/><path d="M12 18v-8"/><path d="M18 18V6"/>'
  };
  var Q_STEPS = [
    { key: 'for', title: 'Who is it for?', opts: [
      { v: 'him', label: 'For him', hint: 'Masculine scents' },
      { v: 'her', label: 'For her', hint: 'Feminine scents' },
      { v: 'any', label: 'Either', hint: 'Unisex, easy to share' } ] },
    { key: 'when', title: 'When will you wear it?', multi: true, note: 'Pick as many as you like.', opts: [
      { v: 'day', label: 'Every day and work', hint: 'Easy, fresh, never too much', icon: 'sun' },
      { v: 'evening', label: 'Evenings and dates', hint: 'Warmer and more memorable', icon: 'moon' },
      { v: 'special', label: 'Special occasions', hint: 'Weddings, parties, big days', icon: 'spark' },
      { v: 'mix', label: 'A bit of everything', hint: 'One bottle for all year', icon: 'loop' } ] },
    { key: 'family', title: 'Which smells do you like?', multi: true, note: 'Pick as many as you like.', opts: [
      { v: 'fresh', label: 'Fresh and clean', hint: 'Citrus, sea air, clean musk', icon: 'leaf' },
      { v: 'sweet', label: 'Sweet and cosy', hint: 'Vanilla, caramel, tonka', icon: 'sweet' },
      { v: 'woody', label: 'Warm and woody', hint: 'Sandalwood, spice, amber', icon: 'flame' },
      { v: 'floral', label: 'Floral and fruity', hint: 'Rose, jasmine, berries', icon: 'flower' },
      { v: 'dark', label: 'Dark and smoky', hint: 'Oud, leather, tobacco', icon: 'smoke' },
      { v: 'any', label: 'Not sure', hint: 'Show me the most loved', icon: 'star' } ] },
    { key: 'strength', title: 'How strong do you like it?', opts: [
      { v: 'soft', label: 'Soft', hint: 'Close to the skin', icon: 'b1' },
      { v: 'mid', label: 'Noticeable', hint: 'People notice up close', icon: 'b2' },
      { v: 'bold', label: 'Bold', hint: 'Fills a room', icon: 'b3' } ] }
  ];
  var Q_SAY = {
    for: { him: 'him', her: 'her', any: 'anyone' },
    when: { day: 'every day', evening: 'evenings', special: 'special occasions', mix: 'all year' },
    family: { fresh: 'fresh and clean', sweet: 'sweet and cosy', woody: 'warm and woody', floral: 'floral and fruity', dark: 'dark and smoky', any: '' },
    strength: { soft: 'soft', mid: 'noticeable', bold: 'bold' }
  };
  var Q_SAY_SHORT = { fresh: 'fresh', sweet: 'sweet', woody: 'woody', floral: 'floral', dark: 'smoky' };
  function listJoin(arr) { arr = arr.filter(Boolean); return arr.length <= 1 ? (arr[0] || '') : arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1]; }
  // Plain words for the answers, e.g. ["him", "evenings and every day", "fresh and woody scents", "noticeable"].
  function aroQuizSay(a) {
    a = a || {};
    var fams = toArr(a.family).filter(function (k) { return k !== 'any'; });
    var fam = fams.length === 1 ? Q_SAY.family[fams[0]] : (fams.length ? listJoin(fams.map(function (k) { return Q_SAY_SHORT[k]; })) + ' scents' : '');
    return [Q_SAY.for[a.for], listJoin(toArr(a.when).map(function (k) { return Q_SAY.when[k]; })), fam, Q_SAY.strength[a.strength]].filter(Boolean);
  }
  // Answers <-> the ?a= address, e.g. him.evening_day.fresh_woody.mid
  function aroQuizKey(a) { return [a.for, toArr(a.when).join('_'), toArr(a.family).join('_'), a.strength].join('.'); }
  function aroQuizRead(key) {
    var parts = String(key || '').split('.');
    if (parts.length !== 4) return null;
    var a = { for: parts[0], when: parts[1].split('_').filter(Boolean), family: parts[2].split('_').filter(Boolean), strength: parts[3] };
    var okList = function (arr, table) { return arr.length > 0 && arr.every(function (x) { return !!table[x]; }); };
    if (!QUIZ.for[a.for] || !okList(a.when, QUIZ.when) || !okList(a.family, QUIZ.family) || !QUIZ.strength[a.strength]) return null;
    return a;
  }
  var Q_CHECK = '<span class="aro-sf__qcheck" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>';

  // Runs the questions inside a panel. opts: { email: true to ask for an email at the end,
  // onDone(answers, email), onClose() }. Returns { start(keep), setAnswers(a), answers() }.
  function aroQuizFlow(panel, opts) {
    if (!panel) return null;
    if (panel.__aroFlow) return panel.__aroFlow;
    opts = opts || {};
    var body = panel.querySelector('[data-aro-q-body]');
    var count = panel.querySelector('[data-aro-q-count]');
    var bar = panel.querySelectorAll('[data-aro-q-bar] span');
    var back = panel.querySelector('[data-aro-q-back]');
    var close = panel.querySelector('[data-aro-q-close]');
    var segs = Q_STEPS.length + (opts.email ? 1 : 0);
    for (var n = 0; n < bar.length; n++) bar[n].style.display = n < segs ? '' : 'none';
    var ans = {}, step = 0, lastEmail = '';

    function setBar(i) { for (var k = 0; k < bar.length; k++) bar[k].className = k < i ? 'is-done' : (k === i ? 'is-now' : ''); }
    function focusTitle() { var t = body.querySelector('.aro-sf__qtitle'); if (t) { try { t.focus({ preventScroll: true }); } catch (e) { t.focus(); } } }
    function optHtml(o, key, multi) {
      var on = multi ? toArr(ans[key]).indexOf(o.v) > -1 : ans[key] === o.v;
      var icon = o.icon ? '<span class="aro-sf__qicon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + Q_ICON[o.icon] + '</svg></span>' : '';
      return '<button type="button" class="aro-sf__qopt" data-v="' + o.v + '" aria-pressed="' + (on ? 'true' : 'false') + '">' + (multi ? Q_CHECK : '') + icon
        + '<span class="aro-sf__qtext"><span class="aro-sf__qlabel">' + o.label + '</span><span class="aro-sf__qhint">' + o.hint + '</span></span></button>';
    }
    function finish(email) {
      var copy = {};
      Object.keys(ans).forEach(function (k) { copy[k] = Array.isArray(ans[k]) ? ans[k].slice() : ans[k]; });
      if (typeof opts.onDone === 'function') opts.onDone(copy, email || '');
    }
    function renderStep() {
      if (step >= Q_STEPS.length) { if (opts.email) renderEmail(); else finish(''); return; }
      var s = Q_STEPS[step];
      setBar(step);
      if (count) count.textContent = 'Question ' + (step + 1) + ' of ' + Q_STEPS.length;
      body.innerHTML = '<fieldset class="aro-sf__qset"><legend class="aro-sf__qtitle" tabindex="-1">' + s.title + '</legend>'
        + (s.note ? '<p class="aro-sf__qnote">' + s.note + '</p>' : '')
        + '<div class="aro-sf__qopts aro-sf__qopts--' + s.opts.length + '">' + s.opts.map(function (o) { return optHtml(o, s.key, s.multi); }).join('') + '</div></fieldset>'
        + (s.multi ? '<button type="button" class="aro-sf__btn aro-sf__qnext" data-aro-q-next' + (toArr(ans[s.key]).length ? '' : ' disabled') + '>Continue</button>' : '');
      if (back) back.hidden = step === 0;
      focusTitle();
    }
    function renderEmail() {
      setBar(Q_STEPS.length);
      if (count) count.textContent = 'Last step';
      body.innerHTML = '<div class="aro-sf__qset"><p class="aro-sf__qtitle" tabindex="-1">Your top 3 are ready</p>'
        + '<p class="aro-sf__qsub">Want them by email too? We\'ll send your matches and the odd offer. Unsubscribe any time.</p>'
        + '<form class="aro-sf__email" data-aro-q-email novalidate>'
        + '<label class="visually-hidden" for="aro-sf-email">Email</label>'
        + '<input id="aro-sf-email" type="email" name="email" autocomplete="email" inputmode="email" placeholder="Your email" value="' + esc(lastEmail) + '">'
        + '<button type="submit" class="aro-sf__btn">Show my matches</button>'
        + '</form>'
        + '<p class="aro-sf__err" data-aro-q-err hidden>Please check your email address, or skip this step.</p>'
        + '<button type="button" class="aro-sf__qskip" data-aro-q-skip>No thanks, just show me</button>'
        + '<p class="aro-sf__qlegal">By signing up you agree to our <a href="/policies/privacy-policy">privacy policy</a>.</p></div>';
      if (back) back.hidden = false;
      focusTitle();
      var f = body.querySelector('[data-aro-q-email]');
      f.addEventListener('submit', function (e) {
        e.preventDefault();
        var inp = f.querySelector('input'), v = inp.value.trim(), err = body.querySelector('[data-aro-q-err]');
        if (!v) { finish(''); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) { inp.setAttribute('aria-invalid', 'true'); err.hidden = false; inp.focus(); return; }
        inp.removeAttribute('aria-invalid'); err.hidden = true;
        lastEmail = v;
        finish(v);
      });
      body.querySelector('[data-aro-q-skip]').addEventListener('click', function () { finish(''); });
    }
    panel.addEventListener('click', function (e) {
      if (step >= Q_STEPS.length || !e.target || !e.target.closest) return;
      var s = Q_STEPS[step];
      if (e.target.closest('[data-aro-q-next]')) { if (toArr(ans[s.key]).length) { step++; renderStep(); } return; }
      var b = e.target.closest('.aro-sf__qopt');
      if (!b || !panel.contains(b)) return;
      var v = b.getAttribute('data-v');
      if (s.multi) {
        var cur = toArr(ans[s.key]).slice(), at = cur.indexOf(v);
        if (at > -1) cur.splice(at, 1);
        else if (v === 'any') cur = ['any'];
        else { cur = cur.filter(function (x) { return x !== 'any'; }); cur.push(v); }
        ans[s.key] = cur;
        body.querySelectorAll('.aro-sf__qopt').forEach(function (x) { x.setAttribute('aria-pressed', cur.indexOf(x.getAttribute('data-v')) > -1 ? 'true' : 'false'); });
        var nx = body.querySelector('[data-aro-q-next]');
        if (nx) nx.disabled = !cur.length;
        return;
      }
      ans[s.key] = v;
      body.querySelectorAll('.aro-sf__qopt').forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
      setTimeout(function () { step++; renderStep(); }, 180);
    });
    if (back) back.addEventListener('click', function () { if (step > 0) { step--; renderStep(); } });
    if (close) close.addEventListener('click', function () { if (typeof opts.onClose === 'function') opts.onClose(); });
    var api = {
      start: function (keep) { if (!keep) ans = {}; step = 0; renderStep(); },
      setAnswers: function (a) { ans = a || {}; },
      answers: function () { return ans; }
    };
    panel.__aroFlow = api;
    return api;
  }

  // ---------- small helpers for rendering ----------
  function money(pence) {
    if (pence == null || pence === '') return '';
    pence = Math.round(Number(pence));
    var pounds = Math.floor(pence / 100), p = pence % 100;
    return '£' + pounds + (p ? '.' + (p < 10 ? '0' : '') + p : '');
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function prettyTag(t) { return t.replace(/\b\w/g, function (c) { return c.toUpperCase(); }).replace('Spring Summer', 'Spring and summer').replace('Autumn Winter', 'Autumn and winter'); }

  // Tags the cart when someone finishes our quiz or finder, the way RevenueHunt's __quiz_id did
  // (RevenueHunt removed 30 Sep 2026). The tags travel to the order ("Additional details" in admin),
  // so quiz sales can be counted. Tags queue one after another; keepalive lets one finish if the
  // shopper leaves the page straight away.
  var tagChain = Promise.resolve();
  function aroTagCart(attrs) {
    var url = ((window.routes && window.routes.cart_update_url) || '/cart/update') + '.js';
    tagChain = tagChain.then(function () {
      return fetch(url, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ attributes: attrs }) });
    }).catch(function () {});
    return tagChain;
  }

  if (!window.__aroQaInit) {
    window.__aroQaInit = true;
    var ADD_URL = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
    var closeAll = function () {
      document.querySelectorAll('.aro-qa.is-open').forEach(function (qa) {
        qa.classList.remove('is-open');
        var t = qa.querySelector('[data-aro-qa-toggle]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    };
    var openDrawer = function () {
      if (typeof window.aroOpenCartDrawer === 'function') { try { window.aroOpenCartDrawer(0); return; } catch (e) {} }
      var cd = document.querySelector('cart-drawer');
      if (cd && typeof cd.open === 'function') { try { cd.open(); } catch (e) {} }
    };
    var addVariant = function (btn) {
      if (!btn || btn.classList.contains('is-busy')) return;
      var id = parseInt(btn.getAttribute('data-aro-qa-add'), 10);
      if (!id) return;
      var html = btn.innerHTML;
      btn.classList.add('is-busy'); btn.setAttribute('aria-busy', 'true'); btn.innerHTML = 'Adding';
      fetch(ADD_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: id, quantity: 1 }) })
        .then(function (r) { return r.json().then(function (j) { j = j || {}; j.__ok = r.ok; return j; }); })
        .then(function (d) {
          if (!d || d.__ok === false || d.status) throw new Error('add failed');
          btn.classList.remove('is-busy'); btn.classList.add('is-added'); btn.innerHTML = 'Added';
          openDrawer();
          if (typeof window.aroRefreshCart === 'function') { try { window.aroRefreshCart(); } catch (e) {} }
          setTimeout(function () { btn.innerHTML = html; btn.classList.remove('is-added'); btn.removeAttribute('aria-busy'); closeAll(); }, 1400);
        })
        .catch(function () {
          btn.classList.remove('is-busy'); btn.innerHTML = 'Try again';
          setTimeout(function () { btn.innerHTML = html; btn.removeAttribute('aria-busy'); }, 1800);
        });
    };
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      var add = t.closest('[data-aro-qa-add]');
      if (add) { e.preventDefault(); addVariant(add); return; }
      var toggle = t.closest('[data-aro-qa-toggle]');
      if (toggle) {
        e.preventDefault();
        var qa = toggle.closest('.aro-qa');
        if (!qa) return;
        var isOpen = qa.classList.contains('is-open');
        closeAll();
        if (!isOpen) { qa.classList.add('is-open'); toggle.setAttribute('aria-expanded', 'true'); }
        return;
      }
      if (t.closest('[data-aro-qa-close]')) { e.preventDefault(); closeAll(); return; }
      if (!t.closest('.aro-qa')) closeAll();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(); });
  }

  window.aroIndex = aroIndex;
  window.aroMatch = aroMatch;
  window.aroFind = aroFind;
  window.aroQuiz = aroQuiz;
  window.aroQuizPicks = aroQuizPicks;
  window.aroQuizFlow = aroQuizFlow;
  window.aroTagCart = aroTagCart;
  window.aroQuizSay = aroQuizSay;
  window.aroQuizKey = aroQuizKey;
  window.aroQuizRead = aroQuizRead;
  window.aroQuizKeys = { for: Object.keys(QUIZ.for), when: Object.keys(QUIZ.when), family: Object.keys(QUIZ.family), strength: Object.keys(QUIZ.strength) };
  window.aroFinderUtil = { norm: norm, money: money, esc: esc, prettyTag: prettyTag, prep: prep, isSellable: isSellable, handleOf: handleOf };
})();
